import { isValidNumber } from 'libphonenumber-js';
import Validator from 'validatorjs';
import { get, isInteger, isNull, isNumber, zipObjectDeep, isEmpty, isObject, head } from 'lodash';
import moment from 'moment';

import { removePlus } from 'modules/user/utils/formatPhoneNumber';
const EMAIL_PATTERN = '^[_A-Za-z0-9-\\+]+(\\.[_A-Za-z0-9-]+)*@[A-Za-z0-9-]+(\\.[A-Za-z0-9]+)*(\\.[A-Za-z]{2,})$';

function validateEmail(value, requirement, attribute) {
  if (!value) {
    this.validator.errors.add(attribute, 'Email is required');
    return false;
  }

  if (!value.match(EMAIL_PATTERN)) {
    const error = this.validator.messages.customMessages[`invalid.${ attribute }`];
    if (error) this.validator.errors.add(attribute, error);
    return false;
  }

  return true;
}

function validatePhoneNumber(value, requirement, attribute) {
  const attributeLabel = get(this, 'validator.messages.attributeNames', {})[this.attribute] || this.attribute;
  const defaultValue = this.ruleValue;
  const isEmptyValue = isEmpty(value) || (!value.countryCode && !value.phone) || (!!defaultValue && !value.phone && removePlus(value.countryCode) === removePlus(defaultValue));
  const rules = this.validator.rules[attribute] || [];
  const requiredRule = rules.find(item => item.name.includes('required'));

  if (isEmptyValue && !isObject(requiredRule)) return true;

  if (isEmptyValue) {
    const error = this.validator.messages.customMessages[`${ requiredRule.name }.${ attribute }`].replace(':attribute', attributeLabel);

    this.validator.errors.add(attribute, error || 'Phone number is required');
    return false;
  }

  const phoneNumber = `+${ value.countryCode }${ value.phone }`;

  if (!isValidNumber(phoneNumber)) {
    const error = this.validator.messages.customMessages[`invalid.${ attribute }`].replace(':attribute', attributeLabel);

    if (error) this.validator.errors.add(attribute, error || 'Invalid phone number');
    return false;
  }

  return true;
}

function validateGreaterOrEqual(value, requirement, attribute) {
  if (!value) {
    this.validator.errors.add(attribute, 'Field is required');
    return false;
  }

  const requiredValue = get(this, `validator.input.${ requirement }`, null);
  if (!requiredValue) return true;

  if (!isNull(requiredValue) && Number(value) < Number(requiredValue)) {
    const error = this.validator.messages.customMessages[`greater_or_equal.${ attribute }`];
    if (error) this.validator.errors.add(attribute, error);
    return false;
  }

  return true;
}

function validateInteger(value, requirement, attribute) {
  if (!isNumber(+value)) {
    this.validator.errors.add(attribute, 'Field is required');
    return false;
  }

  if (!isInteger(+value)) {
    const error = this.validator.messages.customMessages[`int.${ attribute }`];
    if (error) this.validator.errors.add(attribute, error);
    return false;
  }

  return true;
}

function validateUserDateOfBirth(value, requirement, attribute) {
  const momentObj = moment(value, 'D/M/YYYY', true);
  if (momentObj.year() < 1900 || !momentObj.isValid() || momentObj.isAfter(moment())) {
    const error = this.validator.messages.customMessages[`invalid.${ attribute }`];
    if (error) this.validator.errors.add(attribute, error);
    return false;
  }

  return true;
}

function validateWith(value, requirement, attribute) {
  const relatedErrorField = requirement.find(req => !get(this.validator.input, req) || this.validator.errors.errors.hasOwnProperty(req));

  if (relatedErrorField) {
    const error = this.validator.messages.customMessages[`valid_with.${ attribute }`];
    this.validator.errors.add(attribute, error || `${ attribute } is not valid while ${ relatedErrorField } is invalid`);
    return false;
  }

  return true;
}

function validateFileSize(value, requirement, attribute) {
  if (!value || !value.size) return true;

  if (value.size > Number(requirement) * 10e5) {
    const error = this.validator.messages.customMessages[`file_size.${ attribute }`];
    this.validator.errors.add(attribute, error || `${ attribute } should be less than ${ requirement } mb`);
    return false;
  }

  return true;
}

function validateFileType(value, requirement, attribute) {
  if (!value) return true;

  const requiredFileTypes = requirement.split(',');
  const fileType = value.type || head(value.name.split('.').slice(-1));

  if (!requiredFileTypes.includes(fileType)) {
    const error = this.validator.messages.customMessages[`file_type.${ attribute }`];
    this.validator.errors.add(attribute, error || `${ attribute } should be one of type: ${ requiredFileTypes.join(', ') }`);
    return false;
  }

  return true;
}

Validator.register('email', validateEmail, 'Wrong :attribute format');
Validator.register('telephone', validatePhoneNumber, 'Wrong :attribute format');
Validator.register('greater_or_equal', validateGreaterOrEqual, ':attribute should be greater than :greater_or_equal');
Validator.register('int', validateInteger, ':attribute should be a whole number');
Validator.register('dateOfBirth', validateUserDateOfBirth, ':attribute is invalid');
Validator.register('valid_with', validateWith, ':attribute is invalid');
Validator.register('file_size', validateFileSize, ':attribute has exceeded maximum file size');
Validator.register('file_type', validateFileType, ':attribute has invalid file type');

const getFirstErrors = errors => Object.keys(errors).reduce((result, current) => ({
  ...result,
  [current]: errors[current][0],
}), {});

const createValidator = (
  rules,
  customErrorMessages = {},
  attributeNames = {},
  multipleErrorsPerField = false,
) => (data, context) => {

  const translatedErrorMessages = Object.keys(customErrorMessages).reduce((result, current) => ({
    ...result,
    [current]: typeof customErrorMessages[current] === 'object'
      ? context.intl.formatMessage(customErrorMessages[current], customErrorMessages[current].values)
      : customErrorMessages[current],
  }), {});

  const validation = new Validator(data, rules, translatedErrorMessages);

  const translatedAttributes = Object.keys(attributeNames).reduce((result, current) => ({
    ...result,
    [current]: attributeNames[current] && typeof attributeNames[current] === 'object'
      ? context.intl.formatMessage(attributeNames[current])
      : attributeNames[current],
  }), {});

  validation.setAttributeNames(translatedAttributes);

  if (validation.fails()) {
    const flattenErrors = multipleErrorsPerField
      ? validation.errors.all()
      : getFirstErrors(validation.errors.all());

    return zipObjectDeep(Object.keys(flattenErrors), Object.values(flattenErrors));
  }

  return {};
};

export default createValidator;
